import { localWebPushUserSettingStore } from './store/store';
import { getDate } from './utils/getDate';
import { action, select } from 'satcheljs/lib/legacy';
import {
    updateLocalWebPushUserSetting,
    removeLocalWebPushUserSetting,
    getLocalWebPushUserSetting,
} from './localWebPushUserSettingsStorage';
import { LocalWebPushUserSetting } from './schema/LocalWebPushUserSetting';

export interface LocalWebPushUserSettingState {
    store: LocalWebPushUserSetting;
}

export const MAX_UNIQUEDAYS_SESSION_COUNT_FOR_PROMPT: number = 4;
export const MAX_ENABLE_WEBPUSH_PROMPT_COUNT: number = 3;

export const loadLocalWebPushUserSetting = select<LocalWebPushUserSettingState>({
    store: () => localWebPushUserSettingStore,
})(
    action('loadLocalWebPushUserSetting')(function loadLocalWebPushUserSetting(
        windowObj: Window,
        userPreferenceIdentifier: string,
        state?: LocalWebPushUserSettingState
    ) {
        let persistedLocalSetting = getLocalWebPushUserSetting(windowObj, userPreferenceIdentifier);
        if (persistedLocalSetting != null) {
            Object.keys(persistedLocalSetting).forEach(k => {
                state.store[k] = persistedLocalSetting[k];
            });
        } else {
            state.store.userPreferenceIdentifier = userPreferenceIdentifier;
        }
    })
);

export const incrementUniqueDaysSessionCount = select<LocalWebPushUserSettingState>({
    store: () => localWebPushUserSettingStore,
})(
    action('incrementUniqueDaysSessionCount')(function incrementUniqueDaysSessionCount(
        windowObj: Window,
        state?: LocalWebPushUserSettingState
    ) {
        let today = getDate().toDateString();
        if (state.store.lastSessionDate != today) {
            state.store.lastSessionDate = today;
            if (state.store.uniqueDaysSessionCount < MAX_UNIQUEDAYS_SESSION_COUNT_FOR_PROMPT) {
                state.store.uniqueDaysSessionCount++;
            }

            updateLocalWebPushUserSetting(windowObj, state.store);
        }
    })
);

export const cleanupLocalWebpushUserSetting = select<LocalWebPushUserSettingState>({
    store: () => localWebPushUserSettingStore,
})(
    action('cleanupLocalWebpushUserSetting')(function cleanupLocalWebpushUserSetting(
        windowObj: Window,
        userPreferenceIdentifier: string,
        state?: LocalWebPushUserSettingState
    ) {
        removeLocalWebPushUserSetting(windowObj, state.store.userPreferenceIdentifier);
        state.store.userPreferenceIdentifier = userPreferenceIdentifier;
    })
);

export const updateLocalWebPushEnabledStatus = select<LocalWebPushUserSettingState>({
    store: () => localWebPushUserSettingStore,
})(
    action('updateLocalWebPushEnabledStatus')(function updateLocalWebPushEnabledStatus(
        windowObj: Window,
        enabled: boolean,
        userPreferenceIdentifier: string,
        state?: LocalWebPushUserSettingState
    ) {
        state.store.enabled = enabled;
        state.store.userPreferenceIdentifier = userPreferenceIdentifier;
        updateLocalWebPushUserSetting(windowObj, state.store);
    })
);

export const shouldPromptUserToEnableWebPush = select<LocalWebPushUserSettingState>({
    store: () => localWebPushUserSettingStore,
})(function shouldPromptUserToEnableWebPush(
    windowObj: Window,
    state?: LocalWebPushUserSettingState
): boolean {
    return (
        state.store != null &&
        state.store.enabled == undefined &&
        state.store.uniqueDaysSessionCount >= MAX_UNIQUEDAYS_SESSION_COUNT_FOR_PROMPT &&
        state.store.lastPromptDate != getDate().toDateString() &&
        state.store.promptCount <= MAX_ENABLE_WEBPUSH_PROMPT_COUNT
    );
});

export const incrementLocalWebPushPromptCount = select<LocalWebPushUserSettingState>({
    store: () => localWebPushUserSettingStore,
})(
    action('incrementLocalWebPushPromptCount')(function incrementLocalWebPushPromptCount(
        windowObj: Window,
        state?: LocalWebPushUserSettingState
    ) {
        state.store.promptCount++;
        state.store.lastPromptDate = getDate().toDateString();
        updateLocalWebPushUserSetting(windowObj, state.store);
    })
);

export const resetLocalWebPushPromptCount = select<LocalWebPushUserSettingState>({
    store: () => localWebPushUserSettingStore,
})(
    action('resetLocalWebPushPromptCount')(function resetLocalWebPushPromptCount(
        windowObj: Window,
        state?: LocalWebPushUserSettingState
    ) {
        state.store.promptCount = 0;
        state.store.lastPromptDate = null;
        updateLocalWebPushUserSetting(windowObj, state.store);
    })
);
